import { useState } from 'react';
import { downloadBulkTemplate, uploadBulkBookings } from '../../lib/api';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const BulkUploadDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // dry-run result
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setCommitting(false);
  };

  const handleClose = (open) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadBulkTemplate();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nomiconnect_bookings_template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setLoading(true);
    try {
      const res = await uploadBulkBookings(f, true);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const res = await uploadBulkBookings(file, false);
      toast.success(res.data.message);
      onImported?.();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-sage-700" />
            Bulk Upload Bookings
          </DialogTitle>
          <DialogDescription>
            Import past and future bookings from a CSV or Excel file. Status is auto-assigned based on dates
            (past = checked-out, current = checked-in, future = confirmed). Guests are auto-created if their phone
            number doesn&apos;t match an existing record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Step 1: Template */}
          <div className="border border-stone-200 rounded-md p-4 bg-stone-50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-stone-900 text-sm">1. Download the template</p>
                <p className="text-xs text-stone-500 mt-0.5">
                  CSV with all required columns and two example rows. Keep the headers exactly as-is.
                </p>
              </div>
              <Button
                variant="outline" size="sm"
                className="border-stone-300 shrink-0"
                onClick={handleDownloadTemplate}
                data-testid="bulk-download-template-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />Template
              </Button>
            </div>
          </div>

          {/* Step 2: Upload */}
          <div className="border border-stone-200 rounded-md p-4">
            <p className="font-medium text-stone-900 text-sm mb-2">2. Upload your filled file</p>
            <label className="block">
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm"
                onChange={handleFileChange}
                className="block w-full text-sm text-stone-600
                           file:mr-3 file:py-2 file:px-3 file:rounded file:border-0
                           file:text-sm file:font-medium
                           file:bg-sage-700 file:text-white hover:file:bg-sage-800
                           file:cursor-pointer cursor-pointer"
                disabled={loading || committing}
                data-testid="bulk-upload-input"
              />
            </label>
            {file && (
              <p className="text-xs text-stone-500 mt-2">
                <Upload className="inline w-3 h-3 mr-1" />{file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {/* Step 3: Preview */}
          {loading && (
            <div className="flex items-center justify-center py-8 text-stone-500 text-sm">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />Validating rows…
            </div>
          )}

          {preview && (
            <div className="border border-stone-200 rounded-md overflow-hidden" data-testid="bulk-preview">
              <div className="p-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                <p className="text-sm font-medium text-stone-900">3. Preview — {preview.message}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />{preview.succeeded} valid
                  </span>
                  {preview.failed > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5" />{preview.failed} error{preview.failed > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-50 hover:bg-stone-50">
                      <TableHead className="text-xs">Row</TableHead>
                      <TableHead className="text-xs">Guest</TableHead>
                      <TableHead className="text-xs">Room</TableHead>
                      <TableHead className="text-xs">Dates / Status</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.details.map((d) => (
                      <TableRow
                        key={d.row}
                        className={d.ok ? '' : 'bg-red-50'}
                        data-testid={`bulk-preview-row-${d.row}`}
                      >
                        <TableCell className="text-xs text-stone-500 font-mono">{d.row}</TableCell>
                        <TableCell className="text-sm">{d.guest}</TableCell>
                        <TableCell className="text-sm">{d.room}</TableCell>
                        <TableCell className="text-sm">
                          {d.ok ? (
                            <>
                              <span className="text-stone-600">{d.dates}</span>
                              <span className="block text-xs">
                                <span className={`status-badge status-${d.status}`}>{d.status}</span>
                              </span>
                            </>
                          ) : (
                            <span className="text-red-600 text-xs">{d.error}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {d.ok ? `₹${(d.total ?? 0).toLocaleString('en-IN')}` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => handleClose(false)} className="border-stone-300">Cancel</Button>
          <Button
            onClick={handleCommit}
            disabled={!preview || preview.succeeded === 0 || committing}
            className="bg-sage-700 hover:bg-sage-800 text-white"
            data-testid="bulk-commit-btn"
          >
            {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</> : `Import ${preview?.succeeded || 0} Booking${preview?.succeeded === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
